/**
 * The playback controls the States panel and the sheet editor share (spec
 * 035): the four-way mode switch and the "When it ends" menu.
 */
import React from 'react';
import type { PlaybackMode, StatePlayback } from '../../../../Shared/src/@types';
import styles from './Sheets.module.scss';

export const MODE_LABELS: Record<PlaybackMode, string> = { loop: 'Loop', once: 'Once', pingpong: 'Ping-pong', still: 'Still' };
/** The summary's word for a mode ("Spritesheet · Normal · loop"). */
export const MODE_WORDS: Record<PlaybackMode, string> = { loop: 'loop', once: 'once', pingpong: 'ping-pong', still: 'still' };
const MODES: PlaybackMode[] = ['loop', 'once', 'pingpong', 'still'];

export const ModeSwitch: React.FC<{ mode: PlaybackMode; onChange: (mode: PlaybackMode) => void; disabled?: boolean }> = ({ mode, onChange, disabled }) => (
  <div className={styles.switch} role="group" aria-label="Mode" data-testid="playback-mode">
    {MODES.map((m) => (
      <button key={m} type="button" aria-pressed={mode === m} disabled={disabled} onClick={() => { if (m !== mode) onChange(m); }}>
        {MODE_LABELS[m]}
      </button>
    ))}
  </div>
);

/** "When it ends" applies to Once only: Loop and Ping-pong play until the next `state` key (spec 033 D-7). */
export const endsAtAll = (p: StatePlayback): boolean => p.mode === 'once';

/** Where the author goes to change state after a Once (spec 035 D-6); the panel says "state", never "expression". */
export const WHEN_DONE_HINT = 'To change state afterwards, add a state key on the timeline.';

export const WhenDoneSelect: React.FC<{
  playback: StatePlayback;
  onChange: (whenDone: StatePlayback['whenDone']) => void;
  disabled?: boolean;
}> = ({ playback, onChange, disabled }) => (
  <>
    <label className={styles.row}>
      <span>When it ends</span>
      <select
        aria-label="When it ends"
        value={playback.whenDone === 'hold-first' ? 'hold-first' : 'hold-last'}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === 'hold-first' ? 'hold-first' : undefined)}
      >
        <option value="hold-last">Hold the last frame</option>
        <option value="hold-first">Go back to the first frame</option>
      </select>
    </label>
    <p className={styles.muted} data-testid="when-done-hint">{WHEN_DONE_HINT}</p>
  </>
);

/** A playback in another mode; only Once keeps a `whenDone` (spec 035). */
export function withMode(p: StatePlayback, mode: PlaybackMode): StatePlayback {
  if (mode === 'once') return { ...p, mode };
  const { whenDone: _w, ...rest } = p;
  return { ...rest, mode };
}

/** A playback with `whenDone` set, absent for the default (hold the last frame). */
export function withWhenDone(p: StatePlayback, whenDone: StatePlayback['whenDone']): StatePlayback {
  const { whenDone: _old, ...rest } = p;
  return whenDone === undefined || whenDone === 'hold-last' ? rest : { ...rest, whenDone };
}

/** A playback with `speed` set, absent at 1. */
export function withSpeed(p: StatePlayback, speed: number | undefined): StatePlayback {
  const { speed: _old, ...rest } = p;
  return speed === undefined || speed === 1 ? rest : { ...rest, speed };
}
